import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileUpload, fileToUrl } from "@/components/FileUpload";
import {
  Heart, MessageSquare, Send, Trash2, Pin, Sparkles, Dumbbell, Feather, Apple, Users, MessageCircle,
} from "lucide-react";

// Direct WhatsApp handle for the coach — used by the "تحدث مع المدرب" CTA.
// International format without leading "+" so it works inside `wa.me` links.
const COACH_WHATSAPP_NUMBER = "966538069848";
const COACH_WHATSAPP_URL = `https://wa.me/${COACH_WHATSAPP_NUMBER}`;

// 4 categories per user's spec
const CATEGORIES = [
  { v: "", l: "الكل", icon: Sparkles, color: "text-primary" },
  { v: "general", l: "عام", icon: Sparkles, color: "text-blue-600" },
  { v: "home_workout", l: "تمارين منزلية", icon: Dumbbell, color: "text-orange-600" },
  { v: "flexibility", l: "المرونة", icon: Feather, color: "text-purple-600" },
  { v: "nutrition", l: "التغذية", icon: Apple, color: "text-green-600" },
];
const CAT_META = {
  general: { l: "عام", color: "bg-blue-100 text-blue-800", icon: Sparkles },
  home_workout: { l: "تمارين منزلية", color: "bg-orange-100 text-orange-800", icon: Dumbbell },
  flexibility: { l: "المرونة", color: "bg-purple-100 text-purple-800", icon: Feather },
  nutrition: { l: "التغذية", color: "bg-green-100 text-green-800", icon: Apple },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "الآن";
  if (diff < 3600) return `${Math.floor(diff / 60)} د`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} س`;
  return `${Math.floor(diff / 86400)} يوم`;
}

export default function CommunityPage() {
  const { user } = useAuth();

  const [category, setCategory] = useState("");
  const [posts, setPosts] = useState([]);
  const [newBody, setNewBody] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [newCategory, setNewCategory] = useState("general");
  const [showNewDialog, setShowNewDialog] = useState(false);

  const load = async () => {
    const params = { kind: "public" };
    if (category) params.category = category;
    const { data } = await api.get("/community/posts", { params });
    setPosts(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const createPost = async () => {
    if (!newBody.trim()) return;
    try {
      const images = newImage ? [fileToUrl(newImage)] : [];
      await api.post("/community/posts", {
        body: newBody, kind: "public", category: newCategory, images,
      });
      setNewBody(""); setNewImage(null); setNewCategory("general"); setShowNewDialog(false);
      toast.success("تم النشر");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto lg:mx-0">
      {/* Page header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-semibold" data-testid="community-title">المجتمع</h1>
        <p className="text-muted-foreground mt-2 text-sm sm:text-base">
          شارك تجاربك، اطرح أسئلتك، وتفاعل مع باقي الأعضاء.
        </p>
      </div>

      {/* Segmented actions — WhatsApp direct line to the coach lives beside the
          posts filter so it's visible without stealing focus from the feed. */}
      <div
        className="inline-flex p-1 rounded-full bg-secondary border w-full sm:w-auto"
        data-testid="community-tabs"
      >
        <button
          type="button"
          aria-current="page"
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full text-sm font-medium bg-background shadow-sm text-foreground"
          data-testid="tab-posts"
        >
          <Users className="h-4 w-4" /> منشورات المجتمع
        </button>
        <a
          href={COACH_WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="relative flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground transition-all"
          data-testid="tab-coach"
        >
          <MessageCircle className="h-4 w-4" /> تحدث مع المدرب
        </a>
      </div>

      <PostsSection
        user={user}
        category={category}
        setCategory={setCategory}
        posts={posts}
        load={load}
        newBody={newBody} setNewBody={setNewBody}
        newImage={newImage} setNewImage={setNewImage}
        newCategory={newCategory} setNewCategory={setNewCategory}
        showNewDialog={showNewDialog} setShowNewDialog={setShowNewDialog}
        createPost={createPost}
      />
    </div>
  );
}

function PostsSection({
  user, category, setCategory, posts, load,
  newBody, setNewBody, newImage, setNewImage,
  newCategory, setNewCategory, showNewDialog, setShowNewDialog, createPost,
}) {
  return (
    <>
      <div className="flex items-center justify-start">
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogTrigger asChild>
            <Button className="rounded-full gap-2" data-testid="btn-new-post">
              <Send className="h-4 w-4 rtl:-scale-x-100" /> منشور جديد
            </Button>
          </DialogTrigger>
          <DialogContent dir="rtl" className="sm:max-w-lg">
            <DialogHeader><DialogTitle>منشور جديد</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-2">اختر الفئة:</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.filter((c) => c.v).map((c) => {
                    const Icon = c.icon;
                    const active = newCategory === c.v;
                    return (
                      <button
                        key={c.v}
                        type="button"
                        onClick={() => setNewCategory(c.v)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-sm transition-all ${
                          active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"
                        }`}
                        data-testid={`newpost-cat-${c.v}`}
                      >
                        <Icon className="h-3.5 w-3.5" /> {c.l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Textarea value={newBody} onChange={(e) => setNewBody(e.target.value)}
                        placeholder="اكتب هنا..." className="min-h-[120px]" data-testid="new-post-body" />
              <FileUpload kind="image" value={newImage} onChange={setNewImage} label="صورة (اختياري)" testid="new-post-image" />
              <Button onClick={createPost} className="w-full rounded-full" data-testid="new-post-submit">نشر</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category chips filter */}
      <div className="flex flex-wrap gap-2" data-testid="community-category-chips">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = category === c.v;
          return (
            <button
              key={c.v || "all"}
              onClick={() => setCategory(c.v)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm transition-all ${
                active
                  ? "border-primary bg-primary text-primary-foreground shadow-md"
                  : "border-border bg-card hover:border-primary/40"
              }`}
              data-testid={`chip-community-${c.v || "all"}`}
            >
              <Icon className={`h-4 w-4 ${active ? "" : c.color}`} />
              {c.l}
            </button>
          );
        })}
      </div>

      <div className="space-y-4">
        {posts.length === 0 ? (
          <div className="p-12 border border-dashed rounded-xl text-center text-muted-foreground" data-testid="community-empty">
            لا منشورات في هذه الفئة بعد. كن أول من يشارك!
          </div>
        ) : (
          posts.map((p) => <PostItem key={p.id} post={p} onChange={load} currentUser={user} />)
        )}
      </div>
    </>
  );
}

function PostItem({ post, onChange, currentUser }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const cat = post.category || "general";
  const catMeta = CAT_META[cat] || CAT_META.general;
  const CatIcon = catMeta.icon;

  const loadComments = async () => {
    const { data } = await api.get(`/community/posts/${post.id}/comments`);
    setComments(data);
  };
  const toggle = async () => { if (!showComments) await loadComments(); setShowComments(!showComments); };
  const like = async () => { await api.post(`/community/posts/${post.id}/like`); onChange(); };
  const addComment = async () => {
    if (!newComment.trim()) return;
    await api.post(`/community/posts/${post.id}/comments`, { body: newComment });
    setNewComment(""); loadComments(); onChange();
  };
  const del = async () => {
    if (!window.confirm("حذف المنشور؟")) return;
    await api.delete(`/community/posts/${post.id}`);
    toast.success("تم الحذف"); onChange();
  };
  const canDelete = post.user_id === currentUser?.id || currentUser?.roles?.some((r) => ["admin","super_admin","moderator"].includes(r));

  return (
    <Card className="p-5 space-y-3" data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={post.author?.avatar_url} />
          <AvatarFallback>{(post.author?.full_name || "؟").slice(0, 1)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{post.author?.full_name}</span>
            {post.author?.roles?.some((r) => ["admin","super_admin"].includes(r)) && <Badge className="text-xs">المدرب</Badge>}
            <span className={`text-[10px] rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${catMeta.color}`} data-testid={`post-badge-${post.id}`}>
              <CatIcon className="h-2.5 w-2.5" /> {catMeta.l}
            </span>
            {post.pinned && <Badge variant="secondary" className="text-xs gap-1"><Pin className="h-3 w-3" /> مثبّت</Badge>}
            {post.status === "pending" && <Badge variant="outline" className="text-xs">قيد المراجعة</Badge>}
            <span className="text-xs text-muted-foreground me-auto">{timeAgo(post.created_at)}</span>
          </div>
          <p className="mt-2 leading-relaxed whitespace-pre-wrap">{post.body}</p>
          {post.images?.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {post.images.map((src, i) => {
                const url = src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
                return <img key={i} src={url} alt="" className="rounded-lg max-h-64 w-full object-cover" data-testid={`post-image-${post.id}-${i}`} />;
              })}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 pt-2 border-t">
        <Button size="sm" variant="ghost" onClick={like} className="gap-2" data-testid={`btn-like-${post.id}`}>
          <Heart className={`h-4 w-4 ${post.i_liked ? "fill-current text-accent" : ""}`} /> {post.likes_count || 0}
        </Button>
        <Button size="sm" variant="ghost" onClick={toggle} className="gap-2" data-testid={`btn-comments-${post.id}`}>
          <MessageSquare className="h-4 w-4" /> {post.comments_count || 0}
        </Button>
        {canDelete && (
          <Button size="sm" variant="ghost" onClick={del} className="text-destructive ms-auto" data-testid={`btn-delete-${post.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {showComments && (
        <div className="pt-2 space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-3 text-sm">
              <Avatar className="h-8 w-8">
                <AvatarImage src={c.author?.avatar_url} />
                <AvatarFallback>{(c.author?.full_name || "؟").slice(0, 1)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 bg-secondary rounded-2xl px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.author?.full_name}</span>
                  {c.author?.roles?.some((r) => ["admin","super_admin"].includes(r)) && <Badge className="text-[10px]">مدرب</Badge>}
                </div>
                <p>{c.body}</p>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
                      placeholder="اكتب تعليقًا..." className="min-h-[60px]" data-testid={`new-comment-${post.id}`} />
            <Button onClick={addComment} className="rounded-full" data-testid={`btn-submit-comment-${post.id}`}>إرسال</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
